<template>
  <div :class="['input-wrapper', { 'input_error': error }, { 'input_disabled': disabled }]">
    <template v-if="labelHtml">
      <label
        v-if="label"
        :for="id"
        class="input__label"
        v-html="label"
      />
      <span v-if="required" class="input__label-required"> *</span>
    </template>
    <template v-else>
      <label
        v-if="label"
        :for="id"
        class="input__label"
      >
        {{ label }}
        <span v-if="required" class="input__label-required">*</span>
      </label>
    </template>
    <div class="input__field-container">
      <Component
        :is="isTextarea ? 'textarea' : 'input'"
        v-if="isTel"
        :id="id"
        ref="input"
        v-mask="inputMask"
        v-bind="$attrs"
        :value="currentValue"
        :class="['input__field', {textarea: isTextarea}]"
        :tabindex="disabled ? -1 : 1"
        @input="onInput($event.target.value)"
        @focus="error = false"
      />
      <Component
        :is="isTextarea ? 'textarea' : 'input'"
        v-else
        :id="id"
        ref="input"
        v-bind="$attrs"
        :value="currentValue"
        :class="['input__field', {textarea: isTextarea}]"
        @input="onInput($event.target.value)"
        @focus="error = false"
      />
    </div>
    <Transition name="slide-from-top">
      <span
        v-if="error && errorMsg"
        class="input__error-msg"
      >
        {{ errorMsg }}
      </span>
    </Transition>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { mask, tokens } from 'vue-the-mask'

  export default {
    name: 'BaseInput',
    directives: { mask },
    inheritAttrs: false,
    props: {
      value: {
        type: String,
        default: ''
      },
      fieldType: {
        type: String,
        default: ''
      },
      label: {
        type: String,
        default: ''
      },
      errorMsg: {
        type: String,
        default: ''
      },
      disabled: {
        type: Boolean,
        default: false
      },
      defaultValue: {
        type: String,
        default: ''
      },
      labelHtml: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        error: null,
        currentValue: ''
      }
    },
    computed: {
      ...mapGetters(['getPhoneType']),
      id () {
        return `input-${this._uid}`
      },
      required () {
        return 'required' in this.$attrs && this.$attrs.required
      },
      inputMask () {
        if (this.getPhoneType === '+373') {
          return this.$attrs.type === 'tel' ? '+###########' : ''
        } else {
          return this.$attrs.type === 'tel' ? '+380-##-###-##-##' : ''
        }
      },
      isTextarea () {
        return this.$attrs.type === 'textarea'
      },
      isTel () {
        return this.$attrs.type === 'tel'
      }
    },
    watch: {
      defaultValue: {
        handler () {
          this.onInput(this.defaultValue)
          this.$refs.input.value = this.defaultValue
        }
      },
      errorMsg: {
        handler (msg) {
          this.error = !!msg
        },
        immediate: true
      }
    },
    created () {
      this.currentValue = this.value || this.defaultValue
    },
    mounted () {
      tokens.X = { pattern: /[+\d]/, transform: v => v }
    },
    methods: {
      onInput (value) {
        if (this.fieldType === 'number') {
          value = value.replace(/[^\d]/g, '').slice(0, 8)
          this.currentValue = value
          this.$refs.input.value = this.currentValue
          this.$emit('input', this.currentValue)
        }
        this.currentValue = value
        this.$emit('input', this.currentValue)
      }
    }
  }
</script>

<style lang="scss">
  $padding-for-error: 28px;

  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    box-shadow: 0 0 0 30px $c-gray-light inset !important;
  }

  .input {
    &-wrapper {
      position: relative;
      min-width: 290px;
      padding-bottom: $padding-for-error;
    }

    &_disabled {
      pointer-events: none;
    }

    &__label {
      display: inline-block;
      padding-bottom: 1em;
      font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
      font-size: 16px;
      color: #6a6b70;

      &-required {
        color: $c-active-light;
      }
    }

    &__field {
      display: block;
      width: 100%;
      padding: 0.75em 1.3em;
      font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
      font-size: 15px;
      line-height: 1;
      background-color: $c-gray-light;
      border: none;
      border-radius: 4px;
      outline: none;
      caret-color: $c-active-light;
      appearance: none;
      transition: 0.3s;

      &:invalid {
        box-shadow: none;
      }

      &.textarea {
        padding-top: 20px;
        padding-bottom: 20px;
        overflow: auto;
        line-height: 1.4;
        resize: none;

        @include custom-scrollbar();
      }

      &-container {
        position: relative;

        &::after {
          // error icon
          position: absolute;
          top: 50%;
          right: 5%;
          display: inline-block;
          width: 1.5em;
          height: 1.5em;
          padding-top: 0.25em;
          color: $c-white;
          text-align: center;
          content: "!";
          background-color: $c-active;
          border-radius: 50%;
          opacity: 0;
          transition: opacity 0.3s;
          transform: translateY(-50%);
        }
      }

      @include hover {
        background-color: $c-gray;
      }

      &:focus {
        background-color: $c-white;
        box-shadow: 0 2px 10px 0 $c-shadow;
      }
    }

    &__error-msg {
      position: absolute;
      bottom: 23px;
      left: 0;
      height: 0;
      font-size: 12px;
      color: $c-active;
    }

    &_error {
      .input {
        &__field {
          padding-right: 3.5em;
          background-color: $c-error-light;

          &-container {
            &::after {
              opacity: 1;
            }
          }
        }
      }
    }
  }

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  input[type=number] {
    -moz-appearance: textfield;
  }
</style>
